/**
 * Convert a hex color string like "#1e40af" or "#1e4" to a Tailwind-token
 * style HSL string like "224 76% 48%" (no hsl() wrapper, space-separated).
 *
 * Returns null for invalid input. Tailwind tokens consume these via
 * `hsl(var(--primary))` so the wrapper is added at use-site, not here.
 */
export function hexToHsl(hex: string): string | null {
  const cleaned = hex.replace(/^#/, '').trim();
  const expanded =
    cleaned.length === 3
      ? cleaned
          .split('')
          .map((c) => c + c)
          .join('')
      : cleaned;
  if (!/^[0-9a-fA-F]{6}$/.test(expanded)) return null;

  const r = parseInt(expanded.slice(0, 2), 16) / 255;
  const g = parseInt(expanded.slice(2, 4), 16) / 255;
  const b = parseInt(expanded.slice(4, 6), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  let h = 0;
  let s = 0;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r:
        h = (g - b) / d + (g < b ? 6 : 0);
        break;
      case g:
        h = (b - r) / d + 2;
        break;
      case b:
        h = (r - g) / d + 4;
        break;
    }
    h *= 60;
  }

  // Whole-number HSL rounding visibly shifts exact brand colors.
  const channel = (value: number) => Number(value.toFixed(4));
  return `${channel(h)} ${channel(s * 100)}% ${channel(l * 100)}%`;
}
